// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Presents agent launch, latest-result, retry, and bulk-run controls.
 *
 * Contract Review can run several agents over the same documents. Shared
 * controls apply one disabled/running vocabulary everywhere while the owning
 * hook remains responsible for validation, queueing, and durable job state.
 */
import { useMemo, useState } from 'react'
import { AlertCircle, Bot, CheckCircle2, ChevronDown, Eye, Loader2, Play, RotateCcw, X } from 'lucide-react'

import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'
import type { Agent } from '@/subsystems/agents'

import type { AgentRunHistory } from '../hooks/useWorkspaceAgentRuns'

function formatRunDate(value: string | null) {
  if (!value) return ''
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).replace(/, (\d{1,2}:\d{2})/, ' · $1')
}

interface RunAgentsMenuProps {
  agents: Agent[]
  disabledAgentIds: Set<string>
  runningAgentIds?: Set<string>
  triggerLabel?: string
  onRun: (agentIds: string[]) => void
}

export function RunAgentsMenu({
  agents,
  disabledAgentIds,
  runningAgentIds = new Set(),
  triggerLabel = 'Run',
  onRun,
}: RunAgentsMenuProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const runnable = useMemo(
    () => agents.filter((agent) => agent.pipelineId && !disabledAgentIds.has(agent.agentId)),
    [agents, disabledAgentIds],
  )
  const selectedRunnable = runnable.filter((agent) => selected.has(agent.agentId))

  const toggle = (agentId: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(agentId)) next.delete(agentId)
      else next.add(agentId)
      return next
    })
  }

  const toggleAll = () => {
    setSelected((current) => {
      const allSelected = runnable.every((agent) => current.has(agent.agentId))
      return allSelected ? new Set() : new Set(runnable.map((agent) => agent.agentId))
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5">
          <Play className="h-3.5 w-3.5 fill-current" />
          {triggerLabel}
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="docreview-run-menu w-64">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">Run agents</DropdownMenuLabel>
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={toggleAll}>
            {selectedRunnable.length === runnable.length && runnable.length > 0 ? 'Clear' : 'Select all'}
          </button>
        </div>
        <DropdownMenuSeparator />
        {agents.map((agent) => {
          const disabled = !agent.pipelineId || disabledAgentIds.has(agent.agentId)
          return (
            <DropdownMenuItem
              key={agent.agentId}
              disabled={disabled}
              onSelect={(event) => {
                event.preventDefault()
                if (!disabled) toggle(agent.agentId)
              }}
              className="gap-3"
            >
              <Checkbox checked={selected.has(agent.agentId)} aria-label={`Select ${agent.label}`} />
              <Bot className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">{agent.label}</span>
              {runningAgentIds.has(agent.agentId) ? <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin" /> : null}
            </DropdownMenuItem>
          )
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={selectedRunnable.length === 0}
          onSelect={() => {
            onRun(selectedRunnable.map((agent) => agent.agentId))
            setSelected(new Set())
          }}
          className="justify-center bg-primary text-primary-foreground focus:bg-primary/90 focus:text-primary-foreground"
        >
          Run {selectedRunnable.length > 0 ? `${selectedRunnable.length} selected` : 'selected'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface LatestRunsProps {
  runs: AgentRunHistory[]
  runningAgentIds: Set<string>
  onView: (run: AgentRunHistory, version: number) => void
  onRetry?: (run: AgentRunHistory) => void
}

export function LatestRuns({ runs, runningAgentIds, onView, onRetry }: LatestRunsProps) {
  if (runningAgentIds.size > 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {runningAgentIds.size === 1 ? '1 agent running' : `${runningAgentIds.size} agents running`}
      </div>
    )
  }
  if (runs.length === 0) return <span className="text-muted-foreground">—</span>

  const latestAt = runs[0]?.latestAt ?? null
  const failed = runs.filter((run) => run.status === 'error')
  const versions = new Set(runs.flatMap((run) => run.versions.map((version) => version.version)))
  const runCount = runs.reduce((count, run) => count + run.versions.length, 0)
  const failedLabel = `${failed.length} of ${runs.length} ${runs.length === 1 ? 'agent' : 'agents'} failed`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`group h-auto w-full min-w-0 justify-between gap-2 py-1.5 text-left transition-colors ${failed.length ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-50 hover:text-red-700' : ''}`}
          data-testid="cr-latest-runs-trigger"
        >
          {failed.length
            ? <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
            : <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}
          <span className="min-w-0">
            <span className="block truncate text-xs font-medium">
              {failed.length ? failedLabel : `${runs.length} agent${runs.length === 1 ? '' : 's'} · ${runCount} ${runCount === 1 ? 'run' : 'runs'}`}
            </span>
            <span className={`block truncate text-[11px] ${failed.length ? 'text-slate-500' : 'text-muted-foreground'}`}>{formatRunDate(latestAt)}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        data-testid="cr-latest-runs-menu"
        className="w-[25rem] max-w-[86vw] rounded-xl p-1.5 shadow-[0_16px_40px_rgba(2,8,23,0.16),0_3px_8px_rgba(2,8,23,0.06)] duration-150 ease-[cubic-bezier(0.32,0.72,0.32,1)]"
        style={{ transformOrigin: 'top right' }}
      >
        <DropdownMenuLabel className="px-2.5 py-2 text-xs text-slate-500">
          {failed.length ? `${failedLabel} · ` : ''}{runs.length} {runs.length === 1 ? 'agent' : 'agents'} · {runCount} {runCount === 1 ? 'run' : 'runs'} across {versions.size} {versions.size === 1 ? 'version' : 'versions'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {runs.map((run, index) => {
          const isError = run.status === 'error'
          return (
            <div
              key={run.agentId}
              className="animate-in fade-in-0 slide-in-from-top-1 rounded-lg px-2 py-2.5 duration-200 hover:bg-slate-50"
              style={{ animationDelay: `${index * 30}ms`, animationFillMode: 'both' }}
            >
              <div className="flex items-center gap-2.5">
                {isError
                  ? <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
                  : <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}
                <div className="min-w-0 flex-1 truncate text-sm font-semibold">{run.label}</div>
                <div className="shrink-0 text-xs text-slate-400">{formatRunDate(run.latestAt)}</div>
              </div>
              {isError && run.error ? (
                <p className="ml-6 mt-1 text-sm leading-snug text-red-700" data-testid={`cr-run-error-${run.agentId}`}>
                  {run.error}
                </p>
              ) : null}
              <div className="ml-6 mt-2 flex flex-wrap gap-1.5">
                {isError && onRetry ? (
                  <Button size="sm" className="h-7 gap-1.5 px-2.5 text-xs" onClick={() => onRetry(run)}>
                    <RotateCcw className="h-3 w-3" />Retry
                  </Button>
                ) : null}
                {run.versions.map((version) => (
                  <Button
                    key={version.version}
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs"
                    aria-label={`View ${run.label} v${version.version}`}
                    data-testid={`cr-view-run-${run.agentId}-v${version.version}`}
                    onClick={() => onView(run, version.version)}
                  >
                    <Eye className="h-3 w-3" />v{version.version}
                  </Button>
                ))}
              </div>
            </div>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface BulkRunBarProps extends RunAgentsMenuProps {
  selectedCount: number
  onClear: () => void
}

export function BulkRunBar({ selectedCount, onClear, ...runProps }: BulkRunBarProps) {
  if (selectedCount === 0) return null
  return (
    <div data-testid="cr-bulk-run-bar" className="flex items-center justify-between rounded-md border border-primary/20 bg-primary/5 px-3 py-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        {selectedCount} {selectedCount === 1 ? 'document' : 'documents'} selected
        <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-muted-foreground" onClick={onClear}>
          <X className="h-3.5 w-3.5" />Clear
        </Button>
      </div>
      <RunAgentsMenu {...runProps} triggerLabel="Run agents" />
    </div>
  )
}
